import { ICropping } from "../../fields"
import { arrayMin, groupByEquality, selectMany } from "../../utils/array"

export interface TimelineItem<T = ICropping> {

    node: T

    next: TimelineItem<T>[]
    prev: TimelineItem<T>[]

    group: number
    rowIndex: number
    rowSpan: number

    rankLengthFromPrev: number
    rankLengthFromNext: number

}

export function setupItems<T>(nodes: T[], nextSelector: (n: T) => T[], prevSelector: (n: T) => T[]) {

    const items = nodes.map(node => ({
        node,
        next: null,
        prev: null,
        group: -1,
        rowIndex: -1,
        rowSpan: null,
        rankLengthFromPrev: 0,
        rankLengthFromNext: 0
    }) as TimelineItem<T>)

    items.forEach(i => {

        i.next = nextSelector(i.node)
            .map(n => items.find(p => p.node == n))
            .filter(p => !!p)

        i.prev = prevSelector(i.node)
            .map(n => items.find(p => p.node == n))
            .filter(p => !!p)

    })


    items.forEach(i => {
        i.rankLengthFromPrev = groupByEquality(selectMany(i.prev.map(p => p.next)), r => r.node).length
        i.rankLengthFromNext = groupByEquality(selectMany(i.next.map(n => n.prev)), r => r.node).length
    })

    setupGroups(items)

    return items

}


export function createTimelineItems<T>(nodes: T[], nextSelector: (n: T) => T[], prevSelector: (n: T) => T[]): TimelineItem<T>[] {

    const items = setupItems(nodes, nextSelector, prevSelector)

    let groupIndex = 0

    while (true) {

        groupIndex++

        const group = items.filter(i => i.group == groupIndex)
        if (!group.length) break

        const roots = group.filter(g => !g.prev.length)

        roots.forEach(s => setupRowSpan(s))

        normalizeSpans(roots)

        setupRowIndices(roots, 0)

    }

    return items

}

function setupRowSpan<T>(i: TimelineItem<T>) {

    if (!i.next.length) {

        if (i.rowSpan > 0) return;
        i.rowSpan = getRowSpanFromParents(i);

    }
    else {

        var total = 0;

        i.next.forEach(c => {

            setupRowSpan(c);

            var rows = c.rowSpan;

            var parents = Math.max(c.prev.length, 1);

            if (rows % parents) {
                scaleRowSpan(c, parents)
                rows = c.rowSpan;
            }

            total += (rows / parents);

        });

        i.rowSpan = total;

    }


}


function getRowSpanFromParents<T>(i: TimelineItem<T>) {

    if (!i.prev.length) return 1;

    var pe = i.prev.filter(p =>
        p.next.every(t => i.prev.indexOf(t) < 0)
    );

    var cp = 0;
    pe.forEach(p => cp += getRowSpanFromParents(p));

    return cp;

}




function setupRowIndices<T>(items: TimelineItem<T>[], index: number) {

    let prev: TimelineItem<T> = null;

    items.forEach(i => {

        if (i.next.indexOf(prev) >= 0) index--

        setupRowIndex(i, index)

        index += i.rowSpan + (i.rowIndex - index)

        prev = i

    });

}

function setupRowIndex<T>(i: TimelineItem<T>, index: number) {
    if ((i.rowIndex >= 0) && (i.rowIndex <= index)) return
    i.rowIndex = index
    setupRowIndices(i.next, index)
}



function scaleRowSpan<T>(i: TimelineItem<T>, s: number) {
    i.rowSpan *= s
    i.next.forEach(n => scaleRowSpan(n, s))
}

function setupGroups<T>(items: TimelineItem<T>[]) {

    items = items.slice();
    var groupIndex = 0;

    while (items.length) {

        groupIndex++;

        var group = getNextGroup(items[0]);

        group.forEach(i => {
            i.group = (i.group == -1) ? groupIndex : i.group
            items.remove(c => c == i)
        });

    }

}


function getNextGroup<T>(item: TimelineItem<T>) {

    var q = [item];
    var qindex = 0;

    while (qindex < q.length) {

        var rels = q[qindex].next.concat(q[qindex].prev)

        rels.forEach(r => {
            if (q.indexOf(r) < 0) q.push(r);
        });

        qindex++;
    }

    return q;

}


function normalizeSpans<T>(from: TimelineItem<T>[]) {

    function getAll(i: TimelineItem<T>[]): TimelineItem<T>[] {
        return i.concat(selectMany(i.map(n => getAll(n.next || []))))
    }

    const all = getAll(from)
    const minSpan = arrayMin(all, r => r.rowSpan)

    if (all.every(i => (i.rowSpan % minSpan) == 0)) {
        all.forEach(i => {
            if ((i.rowSpan % minSpan) == 0) {
                i.rowSpan /= minSpan
            }
        })
    }


}
